import React, { useCallback } from 'react';
import styled from 'styled-components';

import usePopup from 'hooks/usePopup';
import InlineDialog from 'components/InlineDialog';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import DragAndDropList from 'components/DragAndDropList';
import DragHandle from 'components/DragAndDropList/DragHandle';
import Button from 'kingpin/atoms/Button';
import Card from '../../Common/Card';
import Modal from '@atlaskit/modal-dialog';
import Form from '../../../kingpin/forms/Form';
import FormContent from '../../../kingpin/forms/FormContent';
import FormHeader from '../../../kingpin/forms/FormHeader';

const PopupDiv = styled.div`
  padding: 24px;
`;

const TabsListDiv = styled.div``;

export const TabItemDiv = styled(Card)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  padding: 0px 16px;
  height: 48px;
  background-color: var(--color-bg-primary);
  border: 1px solid var(--color-border-secondary);
`;

const TabItemLeftDiv = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
`;

const Tab = <Tab extends TabTypes.BaseTab>({
  tab,
  onTabRemoved,
  onTabEdit,
  isMovable,
}: {
  tab: Tab;
  onTabRemoved?: (t: Tab) => void;
  onTabEdit?: (t: Tab) => void;
  isMovable: boolean;
}) => {
  return (
    <TabItemDiv>
      <TabItemLeftDiv>
        <div style={{ marginRight: 8 }}>
          {!isMovable && (
            <div style={{ marginRight: 8 }}>
              <Button
                icon="pushPin"
                onClick={window.tokenFunction}
                isDisabled
                type="Ghost"
                size="Small"
              />
            </div>
          )}
          {isMovable && <DragHandle testId={`drag-${tab.key}`} />}
        </div>
        <Typography.Body type="Body 12" isEllipsis>
          {tab.name}
        </Typography.Body>
      </TabItemLeftDiv>
      {onTabEdit && (
        <Button
          size={'Small'}
          type={'Ghost'}
          onClick={() => onTabEdit(tab)}
          testId={`edit-${tab.key}`}
          icon="edit-outline"
        />
      )}
      {onTabRemoved && (
        <Button
          size={'Small'}
          type={'Ghost'}
          onClick={() => onTabRemoved(tab)}
          testId={`delete-${tab.key}`}
          icon="cross"
        />
      )}
    </TabItemDiv>
  );
};

/**
 * In V3, we want to access this via a popup
 */
const Popup = <Tab extends TabTypes.BaseTab>({
  onOrderChanged,
  onTabRemoved,
  onTabEdit,
  fixedTabs,
  variableTabs,
  close,
}: {
  onOrderChanged: (newOrder: Tab[]) => void;
  onTabRemoved: (tab: Tab) => void;
  onTabEdit?: (tab: Tab) => void;
  fixedTabs: Tab[];
  variableTabs: Tab[];
  close: () => void;
}) => {
  return (
    <Modal onClose={close}>
      <Form>
        <FormHeader title={'Manage Screens'} onClose={close} />
        <FormContent>
          <TabsListDiv>
            {fixedTabs.map((t) => (
              <Tab key={t.key} tab={t} isMovable={false} />
            ))}
            <DragAndDropList
              items={variableTabs}
              onOrderChanged={onOrderChanged}
              droppableId={'tabs'}
              renderItem={(tab) => (
                <Tab
                  tab={tab}
                  onTabRemoved={onTabRemoved}
                  onTabEdit={onTabEdit}
                  isMovable
                />
              )}
            />
          </TabsListDiv>
        </FormContent>
        <Row>
          <Button
            size={'Small'}
            type={'Secondary'}
            label={'Close'}
            onClick={close}
          />
        </Row>
      </Form>
    </Modal>
  );
};

const InlineContent = <Tab extends TabTypes.BaseTab>({
  onOrderChanged,
  onTabRemoved,
  onTabEdit,
  fixedTabs,
  variableTabs,
}: {
  onOrderChanged: (newOrder: Tab[]) => void;
  onTabRemoved: (tab: Tab) => void;
  onTabEdit?: (tab: Tab) => void;
  fixedTabs: Tab[];
  variableTabs: Tab[];
}) => {
  return (
    <PopupDiv>
      <Row style={{ marginBottom: 16 }}>
        <Typography.Header type="H5">{`Tabs (${
          fixedTabs.length + variableTabs.length
        })`}</Typography.Header>
      </Row>
      <TabsListDiv>
        {fixedTabs.map((t) => (
          <Tab key={t.key} tab={t} isMovable={false} />
        ))}
        <DragAndDropList
          items={variableTabs}
          onOrderChanged={onOrderChanged}
          droppableId={'tabs'}
          renderItem={(tab) => (
            <Tab
              tab={tab}
              onTabRemoved={onTabRemoved}
              onTabEdit={onTabEdit}
              isMovable
            />
          )}
        />
      </TabsListDiv>
    </PopupDiv>
  );
};

/**
 * In V1 & V2, this is an inline-dialog flow
 */
const InlineDialogButton = <Tab extends TabTypes.BaseTab>({
  onOrderChanged,
  onTabRemoved,
  onTabEdit,
  fixedTabs,
  variableTabs,
}: {
  onOrderChanged: (newOrder: Tab[]) => void;
  onTabRemoved: (tab: Tab) => void;
  onTabEdit?: (tab: Tab) => void;
  fixedTabs: Tab[];
  variableTabs: Tab[];
}) => {
  const { isOpen, open, close } = usePopup();
  const onClick = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [close, isOpen, open]);

  return (
    <>
      <InlineDialog
        placement="bottom-end"
        isOpen={isOpen}
        onClose={close}
        content={
          <InlineContent
            fixedTabs={fixedTabs}
            variableTabs={variableTabs}
            onOrderChanged={onOrderChanged}
            onTabRemoved={onTabRemoved}
            onTabEdit={onTabEdit}
          />
        }
      >
        <Button
          type="Tertiary"
          size="Small"
          onClick={onClick}
          icon="chevron-down"
          testId="manage-tabs-button"
        />
      </InlineDialog>
    </>
  );
};

const ManageTabs = {
  InlineDialogButton,
  Popup,
};

export default ManageTabs;
